import { map, take } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlSegment } from "@angular/router";

import { combineLatest, Observable, of } from "rxjs";
import { UserService } from "./user.service";
import { AccountService } from "../../account/services/account.service";
import { LegalAcceptanceComponent } from "../components/legal-acceptance-modal/legal-acceptance.component";
import { OfflineModalComponent } from "../components/offline-modal/offline-modal.component";
import { config } from "@config";
import { InitService } from "@core-services/init.service";
import { ApicallService } from "@core-services/apicall.service";
import { Modal, ModalService } from "@core-services/modal.service";
import { PSP_REDIRECTION } from "@helpers/constants";
import { CoreStoreFacade } from "@modules/+new/core/core-store/domain/+state/core-store.facade";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { Helper } from "@helpers/functions";

const lastPathKey: string = "last_path";

@Injectable({
	providedIn: "root"
})
export class AuthGuard {
	private whiteListedRoutesWithOutLogin = [
		"login",
		"register",
		"lost_password",
		"lost_password_requested",
		"reset_password",
		"verify_email",
		"logout",
		"verify_device",
		"registered",
		"freeze",
		"resend_registration",
		"provider-zimpler",
		"provider",
		"auth-callback",
		"notifications",
		PSP_REDIRECTION
	];

	private persistableQueryParams = ["utm_source", "utm_medium", "utm_campaign"];

	private blackListedRoutsWithLogin = [
		"login",
		"register",
		"lost_password",
		"lost_password_requested",
		"reset_password",
		"verify_email",
		"registered",
		"resend_registration"
	];

	private whiteListedRoutesWithOutAcceptedLegal = ["account", "logout"];
	private rerouteStarted: boolean = false;
	private redirectToUkWebAppEnabled: Observable<boolean> = this.launchDarklyService.isActive$(
		LD_FLAG.UK_FIN_PROM_REDIRECT
	);
	private isDynamictermAndConditionActive: Observable<boolean> = this.launchDarklyService.isActive$(
		LD_FLAG.TERMS_AND_CONDITIONS
	);
	public constructor(
		protected apicallService: ApicallService,
		protected userService: UserService,
		protected accountService: AccountService,
		protected modalService: ModalService,
		protected router: Router,
		protected initService: InitService,
		private readonly coreStoreFacade: CoreStoreFacade,
		private readonly launchDarklyService: LaunchDarklyService
	) {}

	public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		this.saveCurrentPathForUK();
		this.checkForUTMQueryParams(route);

		if (this.coreStoreFacade.coreStoreSnapshot.isOffline && !config.dryRun) {
			return of(false);
		}

		if (
			!this.userService.user.isLoggedIn &&
			this.userService.redirect == null &&
			state.url
				.split("/")
				.every((url) => this.blackListedRoutsWithLogin.indexOf(url) === -1 && url !== "verify_device")
		) {
			this.userService.redirect = state.url;
		}

		if (!this.userService.user.isLoggedIn) {
			if (this.isWhiteListedRoutesWithOutLogin(route)) {
				this.initService.setWhitelistRoute();

				if (this.coreStoreFacade.hasAccessToken()) {
					return this.checkSession(route);
				}

				return of(true);
			}
			return this.checkSession(route);
		}

		this.redirectToHomeIfBlacklisted(route);
		this.showPrivacyPolicy(route);
		return of(true);
	}

	private isListed = (route: ActivatedRouteSnapshot, list: string[]): boolean =>
		route.url.some((segment: UrlSegment) => list.some((path: string) => path === segment.path));

	private isWhiteListedRoutesWithOutLogin = (route: ActivatedRouteSnapshot): boolean =>
		this.isListed(route, this.whiteListedRoutesWithOutLogin) ||
		this.whiteListedRoutesWithOutLogin.some((path: string) => path === route.routeConfig.path);

	private isWhiteListedRoutesWithOutAcceptedLegal = (route: ActivatedRouteSnapshot): boolean =>
		this.isListed(route, this.whiteListedRoutesWithOutAcceptedLegal);

	private isBlackListedRoutsWithLogin = (route: ActivatedRouteSnapshot): boolean =>
		this.isListed(route, this.blackListedRoutsWithLogin);

	private checkSession(route: ActivatedRouteSnapshot): Observable<boolean> {
		return combineLatest([
			this.userService
				.getSession()
				.pipe(
					map(
						(isSessionValid: boolean) =>
							this.redirectToLogin(isSessionValid) &&
							this.redirectToHomeIfBlacklisted(route, isSessionValid) &&
							this.showPrivacyPolicy(route, isSessionValid)
					)
				),
			this.redirectToWebAppIfNotUk()
		]).pipe(
			map(([session, redirect]) => {
				return session && redirect;
			})
		);
	}

	private redirectToLogin(initialised: boolean): boolean {
		if (!initialised && !this.rerouteStarted) {
			this.rerouteStarted = true;
			this.router.navigateByUrl("/user/login").then(() => (this.rerouteStarted = false));
		}
		return true;
	}

	private redirectToHomeIfBlacklisted(route: ActivatedRouteSnapshot, initialised: boolean = true): boolean {
		if (initialised && this.isBlackListedRoutsWithLogin(route)) {
			this.router.navigateByUrl("/home");
		}
		return true;
	}

	private showPrivacyPolicy(route: ActivatedRouteSnapshot, initialised: boolean = true): boolean {
		if (initialised) {
			if (this.accountService.account == null) {
				this.modalService.showModal(OfflineModalComponent);
			} else if (
				!this.accountService.acceptedTerms() &&
				!this.isWhiteListedRoutesWithOutAcceptedLegal(route) &&
				!this.modalService.stack.some((modal: Modal) => modal.instance instanceof LegalAcceptanceComponent)
			) {
				// Show legal popup
				this.isDynamictermAndConditionActive.pipe(
					take(1),
					map((active) => {
						if (!active) {
							this.modalService.showModal(LegalAcceptanceComponent);
						}
					})
				);
			}
		}
		return true;
	}

	public redirectToWebAppIfNotUk(): Observable<boolean> {
		return this.redirectToUkWebAppEnabled.pipe(
			map((redirect_active) => {
				if (redirect_active && !this.userService.isUkResident()) {
					let last_path = Helper.getCookie(lastPathKey);
					if (last_path !== null) {
						window.location.href = config.redirectWebApp + last_path;
					} else {
						// Fallback
						window.location.href = config.redirectWebApp;
					}
					return false;
				}
				return true;
			})
		);
	}

	private saveCurrentPathForUK(): void {
		// We want to keep the original path ONLY if it's not the auth-callback, to ensure that deeplinks keep working properly
		// This information needs to be saved in a cookie because it can get lost on the auth redirection
		if (!window.location.pathname.includes("auth-callback")) {
			Helper.setCookie(lastPathKey, window.location.pathname + window.location.search, false);
		}
	}

	private checkForUTMQueryParams(route: ActivatedRouteSnapshot): void {
		if (route.routeConfig.path === "my-account") {
			const utmQueryParams = {};
			this.persistableQueryParams.forEach((key) => {
				const paramValue = route.params[key];
				if (paramValue) {
					utmQueryParams[key] = paramValue;
				}
			});
			Helper.setCookie("utm_params", JSON.stringify(utmQueryParams), false);
		}
	}
}
